import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { useActuatorRegistration } from './use-actuator-registration';

// React-Bootstrap Collapse expander. Support only controlled mode
export const BootstrapExpandableActuator = (WrappedComponent) => {
  const HOC = ({
    actuationProps,
    actuatorProps,
    expanded: expandedFromProps,
    onToggle: onToggleFromProps,
    ...restProps
  }) => {
    const needsActivation = useCallback(queryPhase => queryPhase === 'expand', []);
    const activate = useCallback((phase) => {
      if (phase === 'expand') {
        onToggleFromProps(true);
      }
    }, [onToggleFromProps]);

    const { actuatorId, debugMode } = useActuatorRegistration({ activate, needsActivation, actuatorProps });

    const newProps = {
      expanded: expandedFromProps,
      onToggle: onToggleFromProps,
    };

    return (
      <>
        {debugMode && `Exp ID: ${actuatorId}. IsExpanded: ${expandedFromProps}`}
        <WrappedComponent
          {...restProps}
          {...newProps}
          {...WrappedComponent.isActuator ? { actuatorProps, actuationProps } : {}}
        />
      </>
    );
  };

  HOC.propTypes = {
    actuationProps: PropTypes.shape({}),
    actuatorProps: PropTypes.shape({
      registerActuator: PropTypes.func.isRequired,
      unregisterActuator: PropTypes.func.isRequired,
      debugMode: PropTypes.bool,
    }),
    expanded: PropTypes.bool,
    onToggle: PropTypes.func,
  };

  HOC.defaultProps = {
    actuationProps: undefined,
    actuatorProps: {
      debugMode: false,
    },
    expanded: undefined,
    onToggle: undefined,
  };

  hoistNonReactStatics(HOC, WrappedComponent);
  HOC.displayName = `BootstrapExpandableActuator(${getDisplayName(WrappedComponent)})`;
  HOC.isActuator = true;

  return HOC;
};
